/* eslint-disable no-console */
import React, {
  createContext, useContext, useEffect, useCallback, useState
} from 'react';
import jwtDecode from 'jwt-decode';
import { useQueryClient } from 'react-query';
import {
  authenticate, challengeAccepted, challengeAcceptedNoLogin, refreshToken as newToken
} from 'Services/auth.service';
import { APP_LOGIN_ROUTE, env, PERMISSION_WATCH_V2 } from 'Constants';
import { useNotifications } from 'Context/notifications';
import { readServerTime } from 'Components/current-time';

import debug from 'Utils/logger';
import { useStorage, useRouter } from './hooks';

const AuthContext = createContext();

export const AuthProvider = (props) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  // remove rememberMe from cookies since it is not used (at least for now)
  // const [rememberMe, setRememberMe] = useLocalStorage('rememberMe');
  const [rememberMe, setRememberMe] = useState();

  const [token, setToken] = useStorage('cookieStorage', 'authToken');
  const [decodedToken, setDecodedToken] = useState(null);
  const [loginFailure, setLoginFailure] = useState(null);
  const [mfaChallengesResponse, setMfaChallengesResponse] = useState();
  const [mfaChallengeAccepted, setMfaChallengeAccepted] = useState();
  const [previewManagerToken, setPreviewManagerToken] = useStorage('localStorage', 'pmToken');
  const [watchManagerToken, setWatchManagerToken] = useStorage('localStorage', 'wmToken');

  const { showSuccess, showError } = useNotifications();
  const PREVIEW = 'preview';
  const WATCH = 'watch';

  const isLoading = useCallback(() => setLoading(true), []);
  const isNotLoading = useCallback(() => setLoading(false), []);

  const PMAuthFailover = useCallback(async (tokenType, key, username) => {
    const serverList = env.REACT_APP_PREVIEW_MANAGER_URL.split(',').map((item) => item.trim());

    for (let i = 0; i < serverList.length; i++) {
      const server = serverList[i];
      const serverURL = server && server.length > 0 ? `https://${server}` : '';

      try {
        // eslint-disable-next-line no-await-in-loop
        const fetchResponse = await fetch(`${serverURL}/api/token/${key}?user=${encodeURIComponent(username)}`);
        if (fetchResponse.ok) {
          console.log('Connected successfully to', server);
          // eslint-disable-next-line no-await-in-loop
          const result = await fetchResponse.json();
          if (tokenType === PREVIEW) setPreviewManagerToken(result.token);
          else setWatchManagerToken(result.token);
          break; // Break out of for loop when successful
        }
      } catch (error) {
        if (i === serverList.length - 1) {
          if (tokenType === PREVIEW) showError('Failed to connect with a Preview server from the list. All Preview players will be disabled.');
          else showError('Failed to connect with a Watch server from the list. All Watch players will be disabled.');
        } else console.log(`Unable to authenticate with Preview server. ${error.message}. Trying on fallback server...`);
      }
    }
  }, [setPreviewManagerToken, setWatchManagerToken, showError]);

  const onLoginSuccess = useCallback(async ({ data: { token: jwtToken } }, username) => {
    setToken(jwtToken, {
      vEnd: rememberMe ? 30 * 24 * 3600 : undefined,
    });
    isNotLoading();

    // Fail-over logic for watch and preview manager auth token
    PMAuthFailover(PREVIEW, env.REACT_APP_PREVIEW_MANAGER_KEY, username);
    if (PERMISSION_WATCH_V2) PMAuthFailover(WATCH, env.REACT_APP_WATCH_MANAGER_KEY, username);
  }, [setToken, rememberMe, isNotLoading, PMAuthFailover]);

  /**
   * Authenticate user & store jwt data
   */
  const login = useCallback((username, password, mfaChallenge = null, mfaValue = null) => {
    isLoading();
    return authenticate(username, password, mfaChallenge, mfaValue)
      .then((data) => onLoginSuccess(data, username))
      .then(() => {
        setLoginFailure(null);
        setMfaChallengesResponse(null);
        setMfaChallengeAccepted(null);
        showSuccess('Login successful!');
      })
      .catch((e) => {
        console.log(e);
        if (!e.response) {
          setLoginFailure(null);
          showError('Login failed: unable to reach the server. Please try again.');
        } else if (e.response.status === 401) {
          setLoginFailure(null);
          setMfaChallengesResponse(e.response.headers['www-authenticate']);
        } else if (e.response.data?.message?.includes('lockout')) {
          setLoginFailure('lockout');
        } else if (mfaChallenge) {
          setLoginFailure('passcode');
        } else {
          setLoginFailure('credentials');
        }
      })
      .catch(({ response }) => Promise.reject({
        status: response.status,
        ...response.data,
      }));
  }, [isLoading, onLoginSuccess, showSuccess, showError]);

  const acceptMfaChallengeHelper = useCallback((
    username,
    password,
    mfaChallenge,
    apiCall
  ) => (
    apiCall(username, password, mfaChallenge)
      .then((e) => {
        if ('ok' in e.data && e.data.ok) {
          setMfaChallengeAccepted(true);
          setLoginFailure(null);
        } else {
          setLoginFailure('challenge');
        }
      })
      .catch((e) => {
        console.log(e);
        showError('Login failed. Please try again.');
      })
      .catch(({ response }) => Promise.reject({
        status: response.status,
        ...response.data,
      }))
  ), [showError]);

  const acceptMfaChallenge = (username, password, mfaChallenge) => {
    acceptMfaChallengeHelper(username, password, mfaChallenge, challengeAccepted);
  };

  const acceptMfaChallengeNoLogin = (username, uniqueToken, mfaChallenge) => {
    acceptMfaChallengeHelper(username, uniqueToken, mfaChallenge, challengeAcceptedNoLogin);
  };

  const logout = useCallback(() => {
    setToken(undefined);
    setDecodedToken(undefined);
    // clear query cache. Might not be the best place to do it, but must happen on logout
    queryClient.clear();
    router.push(APP_LOGIN_ROUTE);
  }, [setToken, router, queryClient]);

  const refreshToken = useCallback((username) => {
    isLoading();
    newToken().then((data) => onLoginSuccess(data, username));
  }, [onLoginSuccess, isLoading]);

  /**
   * Refresh the jwt halfway through the expiry
   */
  useEffect(() => {
    if (!token || loading) {
      return undefined;
    }

    // decode jwt data
    const data = (() => {
      try {
        return jwtDecode(token);
      } catch (e) {
        return null;
      }
    })();

    // if jwt decode fails, clear the jwt token
    if (!data) {
      logout();
      return undefined;
    }

    const iat = parseInt(data.iat, 10) * 1000;
    const exp = parseInt(data.exp, 10) * 1000;
    const now = new Date(readServerTime()).getTime();

    // if token is expired, clear the jwt token
    if (exp < now) {
      logout();
      return undefined;
    }

    setDecodedToken(data);
    const duration = exp - iat;
    const renewAt = iat + (duration / 2);
    const renewAfter = renewAt - now;
    debug.log('%cRefreshTokenTimer', 'color: green; font-weight: bold', new Date(now + renewAfter));
    // When staying on the same scope (page),
    // do a token refresh after half the duration of the token
    const c = setTimeout(refreshToken, renewAfter, data.user_id);

    // When navigating to a new scope,
    // force refresh the token if it is older than 2.5minutes
    if ((now - iat) > 0.05 * 60 * 1000) {
      refreshToken(data.user_id);
    }

    return () => clearTimeout(c);
  }, [token, logout, loading, refreshToken, setToken]);

  return (
    <AuthContext.Provider
      value={{
        data: decodedToken,
        token,
        loading,
        isAuthenticated: Boolean(token && decodedToken),
        acceptMfaChallenge,
        acceptMfaChallengeNoLogin,
        login,
        logout,
        rememberMe,
        setRememberMe,
        loginFailure,
        mfaChallengesResponse,
        mfaChallengeAccepted,
        setMfaChallengeAccepted,
        previewManagerToken,
        watchManagerToken,
      }}
      {...props}
    />
  );
};

export const useAuth = () => useContext(AuthContext);
